import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  PiArrowRightDuotone,
  PiChartLineDuotone,
  PiCheckCircleDuotone,
  PiCrownDuotone,
  PiGearSixDuotone,
  PiPencilDuotone,
  PiPencilSimpleDuotone,
  PiSparkleDuotone,
  PiSwordDuotone,
  PiTrashDuotone,
  PiTrophyDuotone,
  PiUserPlusDuotone,
  PiUsersDuotone,
} from 'react-icons/pi';
import { deleteChallenge, getChallenges } from '../api/challengeService';
import {
  getRoom,
  getRoomMembers,
  getRoomMemberStatistics,
  updateRoom,
} from '../api/roomService';
import { ACCESS_DENIED_MESSAGE, accessAwareMessage } from '../api/api';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Badge from '../shared/common/Badge';
import Breadcrumb from '../shared/common/Breadcrumb';
import StatCard from '../shared/common/StatCard';
import Modal, { ConfirmModal } from '../shared/common/Modal';
import InviteLinkModal from '../shared/business/room/InviteLinkModal';
import RoomMembersModal from '../shared/business/room/RoomMembersModal';
import AddRoomFriendsModal from '../shared/business/room/AddRoomFriendsModal';
import ScoringRulesSummary from '../shared/business/scoring/ScoringRulesSummary';
import ScoringProfileBuilder, {
  normalizeScoringProfile,
  toScoringProfilePayload,
  validateScoringRules,
} from '../shared/business/scoring/ScoringProfileBuilder';
import { SkeletonGrid } from '../shared/common/Skeleton';
import { useAuth } from '../app/providers/AuthContext';
import { useDelayedLoading } from '../shared/utils/useDelayedLoading';
import {
  appendChallengePrefillSearch,
  hasChallengePrefill,
  readChallengePrefillSearch,
} from '../shared/utils/challengeCreationFlow';
import { getSideBetIndicator } from '../shared/business/challenge/sideBetIndicator';
import { publicUserName, safeUsername } from '../shared/utils/userDisplay';

const STATUS_MAP = { open: 'open', closed: 'closed', resolved: 'resolved' };
const STATUS_LABELS = { open: 'приём ставок', closed: 'ждём результат', resolved: 'завершён' };
const RESULT_LABELS = { WON: 'выигран', LOST: 'не выигран', PENDING: 'ожидает результата' };
const RESULT_BADGE_STATUS = { WON: 'open', LOST: 'resolved', PENDING: 'closed' };
const OWNED_CHALLENGE_BADGE_LABEL = 'Моё пари';
const STAT_EMPTY_VALUE = 0;
const ROOM_STATISTICS_LOAD_ERROR = 'Не удалось загрузить статистику участников';
const ROOM_ACTIONS_MENU_ESCAPE_KEY = 'Escape';
const CHALLENGE_DATE_FALLBACK_TIMESTAMP = Number.MAX_SAFE_INTEGER;
const CHALLENGE_ID_FALLBACK = Number.MAX_SAFE_INTEGER;
const EMPTY_ROOM_STATISTICS = Object.freeze({
  totalScore: 0,
  mainScore: 0,
  sideBetScore: 0,
  mainBetsCount: 0,
  sideBetsCount: 0,
  resolvedBetsCount: 0,
  averageScore: 0,
});

const buildRoomChallengeListPath = (roomId) => `/rooms/${roomId}/challenges`;
const buildRoomStatisticsPath = (roomId) => `/rooms/${roomId}/statistics`;
const buildRoomChallengeCreatePath = (roomId) => `${buildRoomChallengeListPath(roomId)}/create`;
const buildRoomChallengeViewPath = (roomId, challengeId) => `${buildRoomChallengeListPath(roomId)}/${challengeId}`;
const buildRoomChallengeEditPath = (roomId, challengeId) => `${buildRoomChallengeViewPath(roomId, challengeId)}/edit`;
const isResolvedStatus = (status) => status === 'RESOLVED' || status === 'resolved';
const getChallengeEventTimestamp = (challenge) => {
  const timestamp = Date.parse(challenge?.eventStartDate || '');
  return Number.isNaN(timestamp) ? CHALLENGE_DATE_FALLBACK_TIMESTAMP : timestamp;
};
const getChallengeStableId = (challenge) => Number(challenge?.id ?? CHALLENGE_ID_FALLBACK);
const sortChallengesByEventDate = (items) => [...items].sort((left, right) => {
  const dateDiff = getChallengeEventTimestamp(right) - getChallengeEventTimestamp(left);
  if (dateDiff !== 0) return dateDiff;
  return getChallengeStableId(left) - getChallengeStableId(right);
});
const formatStatusLabel = (status) => STATUS_LABELS[status?.toLowerCase?.()] || 'приём ставок';
const formatResultLabel = (result) => RESULT_LABELS[result] || RESULT_LABELS.PENDING;
const resultBadgeStatus = (result) => RESULT_BADGE_STATUS[result] || RESULT_BADGE_STATUS.PENDING;
const challengeScore = (challenge) => Number(challenge?.currentUserScore ?? STAT_EMPTY_VALUE);
const isCreatedByCurrentUser = (challenge, currentUserId) => (
  challenge?.creatorId != null && currentUserId != null && String(challenge.creatorId) === String(currentUserId)
);
const canManageChallenge = (challenge) => challenge?.editable === true && challenge?.managementAvailable === true;
const statisticScore = (statistics) => Number(statistics?.totalScore ?? STAT_EMPTY_VALUE);
const statisticsByUserId = (items) => new Map(
  (Array.isArray(items) ? items : []).map((item) => [String(item.userId), item]),
);
const mergeRoomStatistics = (membersData, statisticsData) => {
  const statsByUserId = statisticsByUserId(statisticsData);
  return (Array.isArray(membersData) ? membersData : [])
    .map((member) => ({
      ...member,
      statistics: statsByUserId.get(String(member.userId)) || EMPTY_ROOM_STATISTICS,
    }))
    .sort((left, right) => statisticScore(right.statistics) - statisticScore(left.statistics));
};
const isLeader = (member, index, members) => (
  index === 0 && statisticScore(member.statistics) > STAT_EMPTY_VALUE
    && statisticScore(member.statistics) === statisticScore(members[0]?.statistics)
);

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString('ru-RU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatChallengePeriod = (challenge) => {
  const start = formatDate(challenge?.eventStartDate);
  const end = formatDate(challenge?.eventEndDate);
  if (start && end) return `${start} — ${end}`;
  return start || end;
};

const RoomStatisticsOverlay = ({ isOpen, onClose, roomId, roomLabel }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !roomId) return;

    setLoading(true);
    setError('');
    setMembers([]);
    Promise.all([
      getRoomMembers(roomId),
      getRoomMemberStatistics(roomId),
    ])
      .then(([membersData, statisticsData]) => {
        setMembers(mergeRoomStatistics(membersData, statisticsData));
      })
      .catch(() => {
        setMembers([]);
        setError(ROOM_STATISTICS_LOAD_ERROR);
      })
      .finally(() => setLoading(false));
  }, [isOpen, roomId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Статистика участников" size="lg">
      <p className="bm-text-secondary bm-text-sm" style={{ marginBottom: 'var(--bm-space-md)' }}>
        {roomLabel}: очки и активность всех участников комнаты.
      </p>

      {error && <div className="bm-auth-error" style={{ marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <p className="bm-text-secondary bm-text-sm">Загрузка...</p>
      ) : members.length === 0 ? (
        <p className="bm-text-secondary bm-text-sm" style={{ textAlign: 'center', padding: '16px 0' }}>
          Статистики пока нет
        </p>
      ) : (
        <div className="bm-room-statistics-list">
          {members.map((member, index) => {
            const statistics = member.statistics || EMPTY_ROOM_STATISTICS;
            const leader = isLeader(member, index, members);

            return (
              <div
                key={member.userId}
                className={`bm-room-statistics-card${leader ? ' bm-room-statistics-card--leader' : ''}`}
              >
                <div className="bm-room-statistics-card__rank">
                  {leader ? <PiCrownDuotone size={20} /> : index + 1}
                </div>
                <div className="bm-room-statistics-card__body">
                  <div className="bm-room-statistics-card__header">
                    <div className="bm-room-statistics-card__identity">
                      <span className="bm-room-statistics-card__name">{publicUserName(member)}</span>
                      {safeUsername(member) && (
                        <span className="bm-room-statistics-card__username">@{safeUsername(member)}</span>
                      )}
                    </div>
                    {leader && <Badge status="primary">Лидер</Badge>}
                  </div>
                  <div className="bm-room-statistics-card__stats" aria-label="Статистика участника">
                    <span className="bm-room-statistics-card__stat">
                      <strong>{statistics.totalScore}</strong>
                      <span>очков</span>
                    </span>
                    <span className="bm-room-statistics-card__stat">
                      <strong>{statistics.mainScore}</strong>
                      <span>основные</span>
                    </span>
                    <span className="bm-room-statistics-card__stat">
                      <strong>{statistics.sideBetScore}</strong>
                      <span>особые</span>
                    </span>
                    <span className="bm-room-statistics-card__stat">
                      <strong>{statistics.resolvedBetsCount}</strong>
                      <span>ставок</span>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

const SideBetIndicator = ({ challenge }) => {
  const indicator = getSideBetIndicator(challenge);
  if (!indicator) return null;

  return (
    <span
      className="bm-text-sm"
      title={indicator.title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 8px',
        borderRadius: 999,
        border: `1px solid ${indicator.border}`,
        background: indicator.background,
        color: indicator.accent,
        fontWeight: 700,
      }}
    >
      <PiSparkleDuotone size={14} />
      {indicator.label}
    </span>
  );
};

export default function LeagueListPage() {
  const { roomId } = useParams();
  const [room, setRoom] = useState(null);
  const [roomChallenges, setRoomChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const showSkeleton = useDelayedLoading(loading);
  const [error, setError] = useState('');
  const [challengeDeleteTarget, setChallengeDeleteTarget] = useState(null);
  const [challengeDeleting, setChallengeDeleting] = useState(false);
  const [roomProfileOpen, setRoomProfileOpen] = useState(false);
  const [roomScoringProfile, setRoomScoringProfile] = useState(null);
  const [roomProfileError, setRoomProfileError] = useState('');
  const [savingRoomProfile, setSavingRoomProfile] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [addFriendsOpen, setAddFriendsOpen] = useState(false);
  const [roomActionsOpen, setRoomActionsOpen] = useState(false);
  const [statisticsOpen, setStatisticsOpen] = useState(false);
  const roomActionsMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const challengePrefill = useMemo(() => readChallengePrefillSearch(location.search), [location.search]);
  const hasPrefilledChallenge = hasChallengePrefill(challengePrefill);
  const roomChallengeCreatePath = appendChallengePrefillSearch(buildRoomChallengeCreatePath(roomId), challengePrefill);
  const roomLabel = room?.name || 'Комната';
  const roomWonScore = Number(room?.currentUserWonScore ?? STAT_EMPTY_VALUE);
  const roomWinCount = Number(room?.currentUserWinCount ?? STAT_EMPTY_VALUE);
  const currentUserId = user?.id ?? user?.userId;
  const activeRoomChallenges = roomChallenges.filter((challenge) => !isResolvedStatus(challenge.status));
  const resolvedRoomChallenges = useMemo(
    () => sortChallengesByEventDate(roomChallenges.filter((challenge) => isResolvedStatus(challenge.status))),
    [roomChallenges],
  );


  const runRoomAction = (action) => {
    setRoomActionsOpen(false);
    action();
  };

  useEffect(() => {
    if (!roomActionsOpen) return undefined;

    const handleDocumentPointerDown = (event) => {
      if (roomActionsMenuRef.current?.contains(event.target)) return;
      setRoomActionsOpen(false);
    };

    const handleDocumentKeyDown = (event) => {
      if (event.key === ROOM_ACTIONS_MENU_ESCAPE_KEY) {
        setRoomActionsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown, true);
    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [roomActionsOpen]);

  useEffect(() => {
    document.title = 'BetMate — Комната';
    Promise.all([getRoom(roomId), getChallenges(roomId)])
      .then(([roomData, challengesData]) => {
        setRoom(roomData);
        setRoomChallenges(Array.isArray(challengesData) ? challengesData : []);
      })
      .catch((err) => setError(accessAwareMessage(err, 'Не удалось загрузить данные')))
      .finally(() => setLoading(false));
  }, [roomId]);

  const openRoomProfileEditor = () => {
    setRoomScoringProfile(normalizeScoringProfile(room?.scoringProfile));
    setRoomProfileOpen(true);
    setRoomProfileError('');
  };

  const closeRoomProfileEditor = () => {
    if (savingRoomProfile) return;
    setRoomProfileOpen(false);
    setRoomScoringProfile(null);
    setRoomProfileError('');
  };

  const handleSaveRoomProfile = async () => {
    if (!room || !roomScoringProfile) return;
    const validationError = validateScoringRules(roomScoringProfile.rules);
    if (validationError) {
      setRoomProfileError(validationError);
      return;
    }

    setSavingRoomProfile(true);
    setRoomProfileError('');
    try {
      const updatedRoom = await updateRoom(roomId, {
        name: room.name,
        description: room.description,
        scoringProfile: toScoringProfilePayload(roomScoringProfile),
      });
      setRoom(updatedRoom);
      setRoomProfileOpen(false);
      setRoomScoringProfile(null);
    } catch (err) {
      setRoomProfileError(accessAwareMessage(err, 'Не удалось сохранить профиль комнаты'));
    } finally {
      setSavingRoomProfile(false);
    }
  };


  const handleChallengeDelete = async () => {
    if (!canManageChallenge(challengeDeleteTarget)) return;
    setChallengeDeleting(true);
    try {
      await deleteChallenge(roomId, null, challengeDeleteTarget.id);
      setRoomChallenges((prev) => prev.filter((challenge) => challenge.id !== challengeDeleteTarget.id));
      setChallengeDeleteTarget(null);
    } catch (err) {
setError(accessAwareMessage(err, 'Не удалось удалить пари'));
    } finally {
      setChallengeDeleting(false);
    }
  };


  if (!loading && error === ACCESS_DENIED_MESSAGE) return (
    <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)' }}>
      <Breadcrumb
        items={[
          { label: 'Комнаты', to: '/rooms' },
          { label: 'Комната недоступна' },
        ]}
      />
      <div className="bm-auth-error">{error}</div>
    </div>
  );

  return (
    <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)' }}>
      <Breadcrumb
        items={[
          { label: 'Комнаты', to: '/rooms' },
          { label: roomLabel },
        ]}
      />

      {/* Описание комнаты */}
      <div className="bm-room-hero">
        <div className="bm-room-hero__text">
          <h1 className="bm-h2 bm-room-hero__title">{room?.name || 'Комната'}</h1>
          {room?.description && (
            <p className="bm-text-secondary" style={{ marginTop: 4 }}>{room.description}</p>
          )}
        </div>
        <div className="bm-room-hero__actions">
          <Button
            variant="secondary"
            size="sm"
            icon={<PiChartLineDuotone size={18} />}
            onClick={() => navigate(buildRoomStatisticsPath(roomId))}
            aria-label="Статистика комнаты"
          >
            Статистика
          </Button>
          <div
            ref={roomActionsMenuRef}
            className="bm-room-actions-menu"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setRoomActionsOpen(false);
              }
            }}
          >
            <Button
              variant="secondary"
              size="sm"
              icon={<PiGearSixDuotone size={18} />}
              onClick={() => setRoomActionsOpen((prev) => !prev)}
              aria-label="Управление комнатой"
              aria-haspopup="menu"
              aria-expanded={roomActionsOpen}
            />
            {roomActionsOpen && (
              <div className="bm-room-actions-menu__panel" role="menu">
                <button
                  className="bm-room-actions-menu__item"
                  type="button"
                  role="menuitem"
                  onClick={() => runRoomAction(() => setMembersOpen(true))}
                >
                  <PiUsersDuotone size={16} />
                  <span>Участники</span>
                </button>
                <button
                  className="bm-room-actions-menu__item"
                  type="button"
                  role="menuitem"
                  onClick={() => runRoomAction(openRoomProfileEditor)}
                >
                  <PiPencilDuotone size={16} />
                  <span>Scoring-профиль</span>
                </button>
                <button
                  className="bm-room-actions-menu__item"
                  type="button"
                  role="menuitem"
                  onClick={() => runRoomAction(() => setAddFriendsOpen(true))}
                >
                  <PiUserPlusDuotone size={16} />
                  <span>Добавить друзей</span>
                </button>
                <button
                  className="bm-room-actions-menu__item"
                  type="button"
                  role="menuitem"
                  onClick={() => runRoomAction(() => setInviteOpen(true))}
                >
                  <PiUserPlusDuotone size={16} />
                  <span>Инвайт-ссылки</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Статистика комнаты */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--bm-space-md)' }}>
        <StatCard
          icon={<PiTrophyDuotone size={22} />}
          value={loading ? '…' : roomWonScore}
          label="Очков в выигранных пари"
          iconColor="var(--bm-accent-gold)"
          iconBg="rgba(255, 209, 102, 0.12)"
          onClick={() => setStatisticsOpen(true)}
          aria-label="Показать статистику участников по очкам"
        />
        <StatCard
          icon={<PiSwordDuotone size={22} />}
          value={loading ? '…' : roomWinCount}
          label="Побед в комнате"
          iconColor="var(--bm-accent-green)"
          iconBg="rgba(0, 214, 143, 0.12)"
          onClick={() => setStatisticsOpen(true)}
          aria-label="Показать статистику участников по победам"
        />
      </section>

      <Card variant="glass" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: 6 }}>
          <div className="bm-font-medium">Профиль комнаты по умолчанию</div>
          <ScoringRulesSummary
            scoringRules={room?.scoringProfile?.rules}
            drawPolicy={room?.scoringProfile?.drawPolicy}
            title="Правила комнаты"
            compact
          />
        </div>
        <Button variant="secondary" size="sm" icon={<PiPencilDuotone size={14} />} onClick={openRoomProfileEditor}>
          Настроить
        </Button>
      </Card>

      {/* Общие действия комнаты */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="primary"
            icon={<PiSwordDuotone size={16} />}
onClick={() => navigate(roomChallengeCreatePath)}
          >
            Создать пари
          </Button>

        </div>
      </div>

      {hasPrefilledChallenge && (
        <Card variant="glass" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <PiSwordDuotone size={22} style={{ color: 'var(--bm-accent-primary)', flexShrink: 0 }} />
          <div>
<div className="bm-font-medium">Событие выбрано для нового пари</div>
            <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 2 }}>
Создай пари в комнате — событие подставится автоматически.
            </p>
          </div>
        </Card>
      )}

      {error && <div className="bm-auth-error">{error}</div>}

      {showSkeleton ? (
        <SkeletonGrid count={4} />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
            <div>
<h3 className="bm-h4">Пари комнаты</h3>
            </div>
            {roomChallenges.length === 0 ? (
              <Card style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <PiSwordDuotone size={28} />
<h4 className="bm-h4">Пари пока нет</h4>
                <p className="bm-text-secondary bm-text-sm bm-truncate">
Создай первое пари — оно появится здесь.
                </p>
                <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--bm-border)' }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<PiSwordDuotone size={14} />}
onClick={() => navigate(roomChallengeCreatePath)}
                    style={{ width: '100%' }}
                  >
                    Создать пари
                  </Button>
                </div>
              </Card>
            ) : (
              <>
                <div className="bm-grid">
                  {activeRoomChallenges.map((challenge) => {
                    const isOwner = isCreatedByCurrentUser(challenge, currentUserId);
                    const canManage = canManageChallenge(challenge);

                    return (
                      <Card
                        key={challenge.id}
                        variant="interactive"
                        onClick={() => navigate(buildRoomChallengeViewPath(roomId, challenge.id))}
                        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Badge status={STATUS_MAP[challenge.status?.toLowerCase()] || 'primary'} pulse={challenge.status === 'OPEN' || challenge.status === 'open'}>
                              {formatStatusLabel(challenge.status)}
                            </Badge>
                            {challenge.currentUserBet && (
                              <PiCheckCircleDuotone size={16} style={{ color: 'var(--bm-accent-green)' }} title="Вы сделали основную ставку" />
                            )}
                            <SideBetIndicator challenge={challenge} />
                            {isOwner && (
                              <Badge status="primary">{OWNED_CHALLENGE_BADGE_LABEL}</Badge>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {canManage && (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<PiPencilSimpleDuotone size={14} />}
                                onClick={(e) => { e.stopPropagation(); navigate(buildRoomChallengeEditPath(roomId, challenge.id)); }}
                                style={{ color: 'var(--bm-accent-primary)', flexShrink: 0 }}
                              />
                            )}
                            {canManage && (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<PiTrashDuotone size={14} />}
                                onClick={(e) => { e.stopPropagation(); setChallengeDeleteTarget(challenge); }}
                                style={{ color: 'var(--bm-accent-red)', flexShrink: 0 }}
                              />
                            )}
                          </div>
                        </div>
                        <h4 className="bm-h4">{challenge.eventTitle || `Событие #${challenge.eventId}`}</h4>
                        {formatChallengePeriod(challenge) && (
                          <p className="bm-text-secondary bm-text-sm">
                            Период: {formatChallengePeriod(challenge)}
                          </p>
                        )}
                        <Badge status={resultBadgeStatus(challenge.currentUserResult)}>
                          {formatResultLabel(challenge.currentUserResult)}
                        </Badge>
                      </Card>
                    );
                  })}
                </div>

                {resolvedRoomChallenges.length > 0 && (
                  <>
                    <h3 className="bm-h4" style={{ marginTop: 'var(--bm-space-lg)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <PiCheckCircleDuotone size={20} style={{ color: 'var(--bm-accent-green)' }} />
История пари комнаты
                    </h3>
                    <div className="bm-grid">
                      {resolvedRoomChallenges.map((challenge) => {
                        const isOwner = isCreatedByCurrentUser(challenge, currentUserId);

                        return (
                          <Card
                            key={challenge.id}
                            variant="interactive"
                            onClick={() => navigate(buildRoomChallengeViewPath(roomId, challenge.id))}
                            style={{
                              display: 'flex', flexDirection: 'column', gap: 10,
                              opacity: 0.75,
                              border: '1px solid rgba(0,214,143,0.2)',
                              background: 'rgba(0,214,143,0.03)',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Badge status="resolved">{formatStatusLabel(challenge.status)}</Badge>
                                <SideBetIndicator challenge={challenge} />
                                {isOwner && (
                                  <Badge status="primary">{OWNED_CHALLENGE_BADGE_LABEL}</Badge>
                                )}
                              </div>
                              {challenge.outcome && (
                                <span className="bm-text-sm" style={{ color: 'var(--bm-accent-green)', fontWeight: 600 }}>
                                  {challenge.outcome}
                                </span>
                              )}
                            </div>
                            <h4 className="bm-h4">{challenge.eventTitle || `Событие #${challenge.eventId}`}</h4>
                            {formatChallengePeriod(challenge) && (
                              <p className="bm-text-secondary bm-text-sm">
                                Период: {formatChallengePeriod(challenge)}
                              </p>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <Badge status={resultBadgeStatus(challenge.currentUserResult)}>
                                {formatResultLabel(challenge.currentUserResult)}
                              </Badge>
                              <span className="bm-text-sm" style={{ color: challengeScore(challenge) > 0 ? 'var(--bm-accent-green)' : 'var(--bm-text-muted)', fontWeight: 700 }}>
                                {challengeScore(challenge)} очк.
                              </span>
                            </div>
                            <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--bm-border)' }}>
                              <Button
                                variant="secondary"
                                size="sm"
                                icon={<PiArrowRightDuotone size={14} />}
                                onClick={(e) => { e.stopPropagation(); navigate(buildRoomChallengeViewPath(roomId, challenge.id)); }}
                                style={{ width: '100%' }}
                              >
                                Посмотреть результаты
                              </Button>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </div>


        </>
      )}


      <ConfirmModal
        isOpen={!!challengeDeleteTarget}
        onClose={() => setChallengeDeleteTarget(null)}
        onConfirm={handleChallengeDelete}
        loading={challengeDeleting}
title="Удалить пари"
        message="Удалить это пари? Все ставки будут потеряны."
      />

      <Modal
        isOpen={roomProfileOpen}
        onClose={closeRoomProfileEditor}
        title="Scoring-профиль комнаты"
        size="lg"
        footer={
          <>
            <Button variant="ghost" type="button" onClick={closeRoomProfileEditor} disabled={savingRoomProfile}>
              Отмена
            </Button>
            <Button variant="primary" type="button" onClick={handleSaveRoomProfile} loading={savingRoomProfile}>
              Сохранить профиль
            </Button>
          </>
        }
      >
        <p className="bm-text-secondary bm-text-sm" style={{ marginBottom: 'var(--bm-space-md)' }}>
          {roomLabel}
        </p>
        <ScoringProfileBuilder
          value={roomScoringProfile}
          onChange={setRoomScoringProfile}
          disabled={savingRoomProfile}
title="Правила будущих пари комнаты"
          description="Профиль комнаты применяется к новым пари, если пари не задаст свои настройки."
          showChangeWarning
          error={roomProfileError}
        />
      </Modal>

      <InviteLinkModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        roomId={roomId}
      />

      <RoomMembersModal
        isOpen={membersOpen}
        onClose={() => setMembersOpen(false)}
        roomId={roomId}
      />

      <RoomStatisticsOverlay
        isOpen={statisticsOpen}
        onClose={() => setStatisticsOpen(false)}
        roomId={roomId}
        roomLabel={roomLabel}
      />

      <AddRoomFriendsModal
        isOpen={addFriendsOpen}
        onClose={() => setAddFriendsOpen(false)}
        roomId={roomId}
      />
    </div>
  );
}
